/**
 * The Starfall Wyrm (M40, docs/RARES.md; Kevin 2026-10-12): "add an ultra rare spawn, 1 in 1,000 waves, with a very over
 * the top model (the same for all levels) which is a flying beast … if killed we should unlock a skin for either the base
 * or one of the towers, one the user doesn't have yet … The enemy should be scaled to the power of the user, giving only
 * around a 50% chance for success." Then: "If you don't manage to kill it, you should be given the opportunity to try
 * again for the equivalent of $25 of game currency." It flies off doing no damage; it rolls everywhere rares roll.
 *
 * - Roll: at a stage's start, wherever a rare could roll (campaign from `wyrmRules.fromLevel`, stages `fromStage` … the
 *   last but one; Iron's blocks; Endless's non-boss waves), an independent draw from the run's seed and the stage (tag
 *   "wyrmStage", never the run's RNG: a run without a Wyrm plays — and hashes — exactly as before) under `wyrmChance`.
 *   At most ONE a run; never while a rare is alive; a stage a rare also rolled is the Wyrm's.
 * - The stage: ONE Wyrm in place of the stage's spawns (Iron: of that block's; the blocks after keep their ticks). A boss
 *   for the rules (`isBoss`: targeting, area ×0.5, no stuns), no boss mechanic; WyrmSpawned, not BossSpawned.
 * - HP: set from the board the moment it appears (sim/wyrmPower.ts `wyrmHp`), kept for every retry.
 * - Rally: as a rare's (sim/rares.ts tickRareOffers), `offers` offers `offerDelayTicks` after its first appearance.
 * - Slain: WyrmKilled; the app grants a Starfall skin the player lacks (meta/wyrmReward.ts; the sim does not know what
 *   the player owns). It reaches the fortress: WyrmFled, it is gone with no damage and no leak, and the action pauses
 *   on `wyrmChoiceOpen` (no stage ends meanwhile) until `Commands.wyrmRetry()` (the app has charged `retryGems`: it
 *   comes again from the portal, no new rally, wounded — with the HP it fled with — when `retryKeepsDamage`, the lead's
 *   call of 2026-10-12 so a $25 retry is worth it; else at full HP) or `Commands.wyrmRelease()` (the run carries on).
 */
import { BalanceData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { RARE_CHANCE_SCALE } from "../data/spawnRules";
import { Command, CommandResult, Commands } from "./command";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { SpawnEntry } from "./waveSchedule";
import { endlessBossWave } from "./runModes";
import { ironRareWave, rareStageSchedule } from "./rares";
import { spawn } from "./systems/enemyMotion";
import { wyrmHp } from "./wyrmPower";

export type WyrmOutcome = "alive" | "killed" | "fled" | "released";

/** The run's Wyrm once it has appeared (null before). `enemyId` is its latest appearance's. */
export interface WyrmRun {
  enemyId: number;
  readonly archetype: number;
  readonly stage: number;
  /** The tick it first appeared. */
  readonly startedTick: number;
  /** Appearances so far: 1, then one more per retry. */
  attempts: number;
  outcome: WyrmOutcome;
  /** Its full HP: the board's power when it first came (every appearance's max HP). */
  readonly hp: Fix64;
  /**
   * The HP it comes back with on a retry: `hp` until it flies off, then what it fled with when `retryKeepsDamage`
   * (it returns wounded), else `hp` again.
   */
  hpLeft: Fix64;
}

const WYRM_TAG = "wyrmStage";

/** The Wyrm's archetype (the one `legend` enemy), or −1 in a bundle without it. */
export function wyrmArchetype(balance: BalanceData): number { return balance.enemies.find((e) => e.legend)?.index ?? -1; }

function stageHash(seed: bigint, stage: number): bigint {
  const h = new Fnv1a64();
  h.writeString(WYRM_TAG);
  h.writeLong(seed);
  h.writeInt(stage);
  return h.value;
}

/** The pure draw: does stage `stage` of a run with this seed bring the Wyrm (when eligible)? */
export function rollsWyrm(balance: BalanceData, seed: bigint, stage: number): boolean {
  const per = balance.spawnRules.wyrm.chancePerMillion;
  return per > 0 && new Pcg32(stageHash(seed, stage)).nextInt(RARE_CHANCE_SCALE) < per;
}

/**
 * Whether the rules let stage `stage` of this run roll the Wyrm: wherever a rare could (sim/rares.ts rareEligible's
 * stages: from `fromStage`, not the boss stage, Iron's blocks before the boss's, Endless's non-boss waves; a campaign
 * level from `fromLevel`), none yet this run, no rare alive.
 */
export function wyrmEligible(sim: LevelSim, stage: number): boolean {
  const W = sim.balance.spawnRules.wyrm;
  // as the rares (MP-20): never on a Daily Road, event or challenge run — every player's road is the same there
  if (sim.debugSandbox || sim.mods.daily !== null || sim.mods.event !== null || sim.mods.challenge !== null || W.chancePerMillion <= 0 || stage < W.fromStage || wyrmArchetype(sim.balance) < 0 || sim.wyrmRun !== null || sim.rare !== null) return false;
  if (sim.isEndless) return !endlessBossWave(sim.balance, stage) && stage < sim.stages;
  const last = sim.isIron ? sim.balance.stagesOf(sim.levelNumber) : sim.stages;
  return sim.levelNumber >= W.fromLevel && stage < last;
}

/**
 * The stage's spawns if it is the Wyrm's (forced by the dev hook, or eligible and drawn), else null; called once per
 * stage start, before the rare's roll. Iron: the whole wave, with the Wyrm in the first block that rolls (and the rare,
 * if one rolls for another block), or null.
 */
export function wyrmStageSchedule(sim: LevelSim, stage: number, startDelay: number): SpawnEntry[] | null {
  if (sim.wyrmRun !== null || sim.wyrmStage > 0) return null;
  const forced = sim.forceWyrmNextStage && !sim.debugSandbox;
  sim.forceWyrmNextStage = false;
  if (sim.isIron) return ironWyrmWave(sim, forced, startDelay);
  if (!forced && !(wyrmEligible(sim, stage) && rollsWyrm(sim.balance, sim.seed, stage))) return null;
  if (wyrmArchetype(sim.balance) < 0) return null;
  sim.wyrmStage = stage;
  return [wyrmEntry(sim, stage, startDelay)];
}

/** Iron (one long wave): the first block from `fromStage` before the boss's that rolls (forced: the first eligible). */
function ironWyrmWave(sim: LevelSim, forced: boolean, startDelay: number): SpawnEntry[] | null {
  const blocks = sim.balance.stagesOf(sim.levelNumber);
  let block = 0;
  for (let b = sim.balance.spawnRules.wyrm.fromStage; b < blocks && block === 0; b++) if (wyrmEligible(sim, b) && (forced || rollsWyrm(sim.balance, sim.seed, b))) block = b;
  if (block === 0 && forced && blocks > 1 && wyrmArchetype(sim.balance) >= 0) block = Math.max(1, Math.min(sim.balance.spawnRules.wyrm.fromStage, blocks - 1));
  if (block === 0) return null;
  sim.wyrmStage = block;
  return rareStageSchedule(sim, 1, startDelay) ?? ironRareWave(sim, startDelay);
}

/** The Wyrm's spawn on stage `stage` (the wave preview shows it once the stage is known to be the Wyrm's). */
export function wyrmEntry(sim: LevelSim, stage: number, tick: number): SpawnEntry {
  const w = wyrmArchetype(sim.balance);
  return { tick, archetype: w, hpMult: wyrmHp(sim, stage, w).div(Fix64.max(sim.balance.enemies[w].hp, Fix64.One)), isBoss: true, pointsOverride: sim.balance.economy.bossPoints, wyrm: true };
}

/** The Wyrm came out of the portal (enemyMotion.spawn): its HP from the board (first time), or `hpLeft` of it (a retry). */
export function onWyrmSpawned(sim: LevelSim, e: Enemy): void {
  const a = sim.balance.enemies[e.archetype];
  let run = sim.wyrmRun;
  if (run === null) {
    const stage = sim.wyrmStage > 0 ? sim.wyrmStage : sim.stage;
    const hp = wyrmHp(sim, stage, e.archetype);
    run = { enemyId: e.id, archetype: e.archetype, stage, startedTick: sim.tick, attempts: 1, outcome: "alive", hp, hpLeft: hp };
    sim.wyrmRun = run;
    const W = sim.balance.spawnRules.wyrm;
    sim.rareOffersLeft = W.offers;
    sim.rareOfferCountdown = W.offerDelayTicks;
  } else {
    run.attempts++;
    run.enemyId = e.id;
    run.outcome = "alive";
  }
  e.maxHp = run.hp;
  e.hp = run.hpLeft;
  e.power = run.hp.div(Fix64.max(a.hp, Fix64.One));
  e.leakDamage = 0;
  sim.events.add(SimEventKind.WyrmSpawned, e.id, run.attempts, e.archetype);
}

/** Whether `e` is the run's Wyrm, alive. */
export function isLiveWyrm(sim: LevelSim, e: Enemy): boolean { return sim.wyrmRun !== null && sim.wyrmRun.outcome === "alive" && sim.wyrmRun.enemyId === e.id; }

/** The Wyrm died (applyDamage). The app grants its reward at once (meta/wyrmReward.ts). */
export function onWyrmKilled(sim: LevelSim, e: Enemy): void {
  const run = sim.wyrmRun!;
  run.outcome = "killed";
  sim.events.add(SimEventKind.WyrmKilled, e.id, run.attempts, e.archetype);
}

/** The Wyrm reached the fortress (moveEnemies, in place of a leak): gone, no damage, and the choice opens. */
export function onWyrmFled(sim: LevelSim, e: Enemy): void {
  const run = sim.wyrmRun!;
  run.outcome = "fled";
  if (sim.balance.spawnRules.wyrm.retryKeepsDamage) run.hpLeft = Fix64.max(e.hp, Fix64.One);
  sim.wyrmChoiceOpen = true;
  sim.events.add(SimEventKind.WyrmFled, e.id, run.attempts, e.archetype);
}

/** `Commands.wyrmRetry()`: it comes again from the portal, now, with `hpLeft` of its full HP (no new rally). */
export function wyrmRetry(sim: LevelSim): CommandResult {
  const run = sim.wyrmRun;
  if (!sim.wyrmChoiceOpen || run === null) return CommandResult.NoWyrmChoice;
  sim.wyrmChoiceOpen = false;
  sim.events.add(SimEventKind.WyrmRetried, run.attempts + 1, run.hpLeft.floorToInt());
  spawn(sim, { tick: sim.tick, archetype: run.archetype, hpMult: run.hp.div(Fix64.max(sim.balance.enemies[run.archetype].hp, Fix64.One)), isBoss: true, pointsOverride: sim.balance.economy.bossPoints, wyrm: true });
  return CommandResult.Ok;
}

/** `Commands.wyrmRelease()`: let it go; the choice closes, its rally ends and the run carries on. */
export function wyrmRelease(sim: LevelSim): CommandResult {
  const run = sim.wyrmRun;
  if (!sim.wyrmChoiceOpen || run === null) return CommandResult.NoWyrmChoice;
  sim.wyrmChoiceOpen = false;
  run.outcome = "released";
  sim.rareOffersLeft = 0;
  sim.events.add(SimEventKind.WyrmReleased, run.attempts);
  return CommandResult.Ok;
}

/**
 * The scripted and strategic players' answer to an open Wyrm choice: retry while `retriesLeft` > 0 (the balance tools
 * measure the retry odds), else release; null when no choice is open.
 */
export function wyrmChoiceCommand(sim: LevelSim, retriesLeft: number): Command | null {
  if (!sim.wyrmChoiceOpen || sim.isOver) return null;
  return retriesLeft > 0 ? Commands.wyrmRetry() : Commands.wyrmRelease();
}
